import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BadgeEuro, Users, Landmark, ShieldCheck, AlertTriangle, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const fmt = (n) => Number(n || 0).toFixed(2);

export default function SubscriptionPage() {
  const { t, i18n } = useTranslation();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/subscription");
      setSub(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pay = async (plan) => {
    setPaying(plan);
    try {
      const { data } = await api.post("/subscription/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying("");
    }
  };

  if (loading) return <Loader2 className="animate-spin" style={{ margin: "3rem auto", color: "#9a7530" }} />;
  if (!sub) return null;

  const dateFmt = (iso) => (iso ? new Date(iso).toLocaleDateString(i18n.language, { day: "2-digit", month: "long", year: "numeric" }) : "—");
  const now = new Date();
  const inGrace = !sub.exempt && new Date(sub.paid_until) < now && !sub.blocked;

  return (
    <div data-testid="subscription-page">
      <h1 style={{ fontFamily: "Cinzel, serif", fontSize: "1.4rem", letterSpacing: "0.14em", color: "#0d1b2a" }}>
        {t("subscription.title", "ABONAMENT")}
      </h1>
      <p className="kv-label" style={{ marginBottom: "1.2rem" }}>
        {t("subscription.subtitle", "Costul platformei se calculează după membrii mobili și lojile copil ale obedienței.")}
      </p>

      {/* Status banner */}
      {sub.exempt ? (
        <div className="gold-card" style={{ padding: "1rem", borderLeft: "4px solid #3e6a3c", marginBottom: "1rem" }} data-testid="sub-exempt-banner">
          <ShieldCheck size={18} style={{ color: "#3e6a3c", display: "inline", marginRight: 8, verticalAlign: "-4px" }} />
          <strong>{t("subscription.exemptTitle", "Abonament GRATUIT")}</strong>
          <p className="kv-label" style={{ marginTop: 4 }}>
            {t("subscription.exemptManualBody", "Obediența ta a fost exonerată de plată de către administratorul platformei — nu plătești nimic.")}
          </p>
        </div>
      ) : sub.blocked ? (
        <div className="gold-card" style={{ padding: "1rem", borderLeft: "4px solid #7c2a2a", marginBottom: "1rem" }} data-testid="sub-blocked-banner">
          <AlertTriangle size={18} style={{ color: "#7c2a2a", display: "inline", marginRight: 8, verticalAlign: "-4px" }} />
          <strong style={{ color: "#7c2a2a" }}>{t("subscription.blockedTitle", "BLOCAT PENTRU NEPLATĂ")}</strong>
          <p className="kv-label" style={{ marginTop: 4 }}>
            {t("subscription.blockedBody", "Aplicația mobilă a membrilor este blocată. Achită abonamentul pentru reactivare imediată.")}
          </p>
        </div>
      ) : inGrace ? (
        <div className="gold-card" style={{ padding: "1rem", borderLeft: "4px solid #b3781f", marginBottom: "1rem" }} data-testid="sub-grace-banner">
          <AlertTriangle size={18} style={{ color: "#b3781f", display: "inline", marginRight: 8, verticalAlign: "-4px" }} />
          <strong>{t("subscription.graceTitle", "Perioadă de grație")}</strong>
          <p className="kv-label" style={{ marginTop: 4 }}>
            {t("subscription.graceBody", "Abonamentul a expirat. Aplicația membrilor se blochează la")} {dateFmt(sub.grace_until)}.
          </p>
        </div>
      ) : (
        <div className="gold-card" style={{ padding: "1rem", borderLeft: "4px solid #3e6a3c", marginBottom: "1rem" }} data-testid="sub-active-banner">
          <ShieldCheck size={18} style={{ color: "#3e6a3c", display: "inline", marginRight: 8, verticalAlign: "-4px" }} />
          <strong>{t("subscription.activeTitle", "Abonament activ")}</strong>
          <span className="kv-label" style={{ marginLeft: 8 }}>
            {t("subscription.paidUntil", "Plătit până la")}: <strong>{dateFmt(sub.paid_until)}</strong>
          </span>
        </div>
      )}

      {/* Counts */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.9rem", marginBottom: "1.2rem" }}>
        <div className="gold-card" style={{ padding: "1rem" }}>
          <Users size={16} style={{ color: "#9a7530" }} />
          <p style={{ fontSize: "1.6rem", fontWeight: 700, color: "#0d1b2a" }} data-testid="sub-mobile-count">{sub.mobile_members}</p>
          <p className="kv-label">{t("subscription.mobileMembers", "Membri mobili")} · {fmt(sub.prices.member_month)} € / {t("subscription.month", "lună")}</p>
        </div>
        <div className="gold-card" style={{ padding: "1rem" }}>
          <Landmark size={16} style={{ color: "#9a7530" }} />
          <p style={{ fontSize: "1.6rem", fontWeight: 700, color: "#0d1b2a" }} data-testid="sub-lodge-count">{sub.child_lodges}</p>
          <p className="kv-label">{t("subscription.childLodges", "Loji copil")} · {fmt(sub.prices.lodge_month)} € / {t("subscription.month", "lună")}</p>
        </div>
      </div>

      {/* Payment plans */}
      {!sub.exempt && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "0.9rem" }}>
          {[
            { plan: "monthly", total: sub.monthly_total, label: t("subscription.payMonthly", "PLATĂ LUNARĂ"), per: t("subscription.perMonth", "€ / lună") },
            { plan: "annual", total: sub.annual_total, label: t("subscription.payAnnual", "PLATĂ ANUALĂ"), per: t("subscription.perYear", "€ / an") },
          ].map(({ plan, total, label, per }) => (
            <div key={plan} className="gold-card" style={{ padding: "1.2rem", textAlign: "center" }}>
              <p style={{ fontFamily: "Cinzel, serif", fontSize: "0.75rem", letterSpacing: "0.16em", color: "#9a7530" }}>{label}</p>
              <p style={{ fontSize: "2rem", fontWeight: 700, color: "#0d1b2a", margin: "0.4rem 0" }} data-testid={`sub-total-${plan}`}>
                {fmt(total)} <span style={{ fontSize: "0.85rem" }}>{per}</span>
              </p>
              <button
                type="button"
                className="btn-primary-gold flex items-center gap-2"
                style={{ margin: "0 auto" }}
                onClick={() => pay(plan)}
                disabled={!!paying}
                data-testid={`sub-pay-${plan}`}
              >
                <CreditCard size={14} />
                {paying === plan ? t("common.loading", "Se încarcă...") : t("subscription.payNow", "PLĂTEȘTE CU CARDUL")}
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="kv-label" style={{ fontSize: "0.68rem", marginTop: "1.2rem" }}>
        {t("subscription.hint", "Plata se procesează securizat prin Stripe. După confirmare, valabilitatea se prelungește automat (30 de zile / 365 de zile). Blocarea pentru neplată intervine la 7 zile după expirare.")}
      </p>
    </div>
  );
}
